import { EditOutlined, ExportOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  getDefaultSortOrder,
  Input,
  Modal,
  notification,
  Select,
  Spin,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import { IResourceComponentsProps, useSelect } from "@pankod/refine-core";
import routerProvider from "@pankod/refine-react-router-v6";
import {
  getAccountingforContributor,
  updateNumberUploads,
} from "apis/accounting/getApiAccounting";
import {
  APIhandleAfterAllPayout,
  APIhandleAfterPayout,
} from "apis/tokenValue/getTokenValue";
import dayjs from "dayjs";
import { useSelectTime } from "hooks/useSelectTime";
import { debounce } from "lodash";
import { ContributorSubscriptionPoolList } from "pages/contributorSubscriptionPool";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { ITrackResponse } from "types/track.interface";
import { IUser } from "types/user.interface";
import { convertSortToQuery } from "utils/convertSortToQueryString";
import styles from "./styles.module.scss";
import useMediaScreen from "hooks/useMediaScreen";

export const AccountingList: React.FC<IResourceComponentsProps> = () => {
  const [filter, setFilter] = useState("");
  const [labelId, setLabelId] = useState("");
  const [contributorsId, setContributorsId] = useState([]);
  const { startTime, endTime, onChangTime, startDate, endDate } =
    useSelectTime();
  const [contributors, setContributors] = useState<any>(null);
  const [loading, setLoading] = React.useState(Boolean);
  const [isLoadingPayout, setLoadingPayout] = useState<any>(false);
  const [accountingId, setAccountingId] = useState<any>("");
  const [sort, setSort] = useState<string>("");
  const [totalEarningsExport, setTotalEarningsExport] = useState<number>(0);
  const { isMobile } = useMediaScreen();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploads, setUploads] = useState<number>(0);
  const [accountingPeriodId, setAccountingPeriodId] = useState<string>("");
  const [mixinitOnly, setMixinitOnly] = useState<string>(""); // '' | 'true' | 'false'

  const [dateTime, setDateTime] = useState<string>(
    dayjs().utc().format("YYYY-MM")
  );

  const { options: optionsContributors } = useSelect<IUser>({
    resource: "users/contributor",
    optionLabel: "username",
    optionValue: "_id",
  });

  useEffect(() => {
    const total = contributors
      ? contributors.reduce(
          (sum: number, contributor: any) =>
            sum + Number(contributor.totalEarningsExport),
          0
        )
      : 0;
    setTotalEarningsExport(total);
  }, [contributors]);

  const { tableProps, sorter }: any = useTable<ITrackResponse>({
    resource: "",
  });

  useEffect(() => {
    setSort(convertSortToQuery(sorter && sorter.length > 0 ? sorter[0] : []));
  }, [sorter]);

  const handleChangeLabel = (value: any) => {
    setLoading(true);
    setLabelId(value);
  };

  const handleChangeContributors = (value: any) => {
    setLoading(true);
    setContributorsId(value);
  };

  const handleChangePlatform = (value: string) => {
    setLoading(true);
    setMixinitOnly(value);
  };

  const { options } = useSelect<any>({
    resource: "labels",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });

  const handleData = useCallback(async () => {
    const mixinitParam =
      mixinitOnly === "" ? undefined : (mixinitOnly as "true" | "false");
    const data = await getAccountingforContributor(
      {
        search: filter,
        startTime: startTime,
        endTime: endTime,
        labelId,
        contributors: contributorsId,
      },
      `accounting`,
      sort,
      mixinitParam
    );

    const customData = data?.data?.data.map((i: any) => {
      return {
        ...i,
        contributorExport: i?.contributor?.username || "",
        metQuotaExport: i?.metQuota ? "YES" : "NO",
        paypalEmailExport: i?.contributor?.paypalEmail || "",
        accountingPeriodExport: i?.name || "",
        totalEarningsExport: +i?.totalEarningsExport || 0,
        currencyExport: "USD",
        statusExport: i?.isAlreadyPayout ? "PAID" : "NOT PAID",
        processPayoutExport: i?.isAlreadyPayout
          ? "PROCESSED"
          : "PROCESS PAYOUT",
      };
    });
    setContributors(customData || []);
  }, [filter, startTime, endTime, labelId, contributorsId, sort, mixinitOnly]);

  useEffect(() => {
    setLoading(contributors === null);
  }, [contributors]);

  useEffect(() => {
    handleData();
  }, [handleData, sort]);

  const { Link } = routerProvider;

  const dataTable = useMemo(() => {
    return {
      dataSource: contributors,
      loading: loading,
    };
  }, [contributors, loading]);

  const handleTimeChange = useCallback(
    (value: any) => {
      setLoading(true);
      onChangTime(value);
    },
    [onChangTime]
  );

  const handlePayout = async (accountingId: any) => {
    setLoadingPayout(true);
    setAccountingId(accountingId);
    const payload = {
      accountingId,
    };
    const res = await APIhandleAfterPayout(payload);

    const filter = contributors?.map((i: any) => {
      const isAlreadyPayout =
        res?.accounting?._id === i?._id ? true : i?.isAlreadyPayout;

      return {
        ...i,
        contributorExport: i?.contributor?.username || "",
        metQuotaExport: i?.metQuota ? "YES" : "NO",
        paypalEmailExport: i?.contributor?.paypalEmail || "",
        totalEarningsExport: +i?.totalEarningsExport || 0,
        currencyExport: "USD",
        accountingPeriodExport: i?.name || "",
        statusExport: isAlreadyPayout ? "PAID" : "NOT PAID",
        isAlreadyPayout,
        processPayoutExport: isAlreadyPayout ? "PROCESSED" : "PROCESS PAYOUT",
      };
    });
    setContributors(filter);
    setLoadingPayout(false);
    notification.open({
      type: "success",
      message: "Notification",
      description: "Process successfully",
    });
  };

  const handleAllPayout = async () => {
    const accountingIds: any = dataTable?.dataSource.map(
      (item: any) => item?._id
    );
    setLoadingPayout(true);
    try {
      const payload = {
        accountingIds,
      };
      const res = await APIhandleAfterAllPayout(payload);
      const filter = contributors?.map((i: any) => {
        const existingAccountingIndex = res.findIndex(
          (item: any) => item?.accounting?._id === i?._id
        );

        return {
          ...i,
          contributorExport: i?.contributor?.username || "",
          metQuotaExport: i?.metQuota ? "YES" : "NO",
          paypalEmailExport: i?.contributor?.paypalEmail || "",
          totalEarningsExport: +i?.totalEarningsExport || 0,
          currencyExport: "USD",
          accountingPeriodExport: i?.name || "",
          statusExport: existingAccountingIndex >= 0 ? "PAID" : "NOT PAID",
          isAlreadyPayout: existingAccountingIndex >= 0,
          processPayoutExport:
            existingAccountingIndex >= 0 ? "PROCESSED" : "PROCESS PAYOUT",
        };
      });

      setContributors(filter);
      notification.open({
        type: "success",
        message: "Notification",
        description: "Process all successfully",
      });
    } catch (error) {
      notification.open({
        type: "error",
        message: "Notification",
        description: "Process all error",
      });
    } finally {
      setLoadingPayout(false);
    }
  };

  const csvReport = useMemo(() => {
    const data: any = dataTable?.dataSource;

    if (!data) {
      return { data: [], headers: [], filename: "" };
    }

    // Function to extract accounting period (first part before any hyphen)
    const extractAccountingPeriod = (accountingPeriod: string): string => {
      // Extract only the date part (e.g., "09/2024" from "09/2024 - Video Vault")
      return accountingPeriod.split(" - ")[0];
    };

    // Function to format amount to 2 decimal places
    const formatAmount = (amount: number): number => {
      return parseFloat(amount.toFixed(2));
    };

    const transformedData = data.reduce((acc: any[], current: any) => {
      const paypalEmail = current.contributor.paypalEmail;
      const accountingPeriod = extractAccountingPeriod(
        current.accountingPeriodExport || current.name
      ); // Dynamically get accounting period
      const amount = formatAmount(current.totalEarningsExport); // Use totalEarningsExport and format amount to 2 decimals
      const currency = "USD"; // Fixed to USD as mentioned

      // Debugging info
      console.log(
        `Processing item: ${paypalEmail}, ${amount}, ${accountingPeriod}`
      );

      // Find existing entry by PayPal email and sum the amounts
      const existing = acc.find((entry) => entry[0] === paypalEmail);
      if (existing) {
        existing[1] = formatAmount(existing[1] + amount); // Sum amounts and format to 2 decimals
      } else {
        acc.push([paypalEmail, amount, currency, accountingPeriod]);
      }

      return acc;
    }, []);

    if (transformedData.length === 0) {
      console.log("No payout items were found after processing");
    }

    const headers = ["Paypal Email", "Amount", "Currency", "Accounting Period"];
    const filename = `Accounting_${startDate}-${endDate}.csv`;

    return {
      data: transformedData,
      headers: headers,
      filename,
    };
  }, [dataTable?.dataSource, endDate, startDate]);

  const debouncedSetFilter = debounce((e: any) => {
    setFilter(e.target.value);
  }, 500);

  const showModal = (id: string, number: number) => {
    setUploads(number || 0);
    setAccountingPeriodId(id);
    setTimeout(() => {
      setIsModalVisible(true);
    }, 10);
  };

  const handleOk = useCallback(async () => {
    const data = await updateNumberUploads({ accountingPeriodId, uploads });
    if (data) {
      setIsModalVisible(false);
      handleData();
      notification.open({
        type: "success",
        message: "Notification",
        description: "Save successfully",
      });
    } else
      notification.open({
        type: "error",
        message: "Notification",
        description: "Save error",
      });
  }, [accountingPeriodId, handleData, uploads]);

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <ContributorSubscriptionPoolList />
      </div>
      <div style={{ fontWeight: 700, color: "black" }}>ACCOUNTING PERIOD</div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className={styles.flex} style={{ alignItems: "center" }}>
          <div style={{ marginRight: "50px" }}>
            <input
              type="month"
              max={dayjs().utc().format("YYYY-MM")}
              value={dateTime}
              onChange={(value) => {
                handleTimeChange(value.target.value);
                setDateTime(value.target.value || dayjs().format("YYYY-MM"));
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "start" : "center",
            }}
          >
            <Form.Item
              label="Label"
              style={{ width: "30%", marginLeft: isMobile ? "0" : "15px" }}
              name="contributors"
            >
              <Select
                style={{ width: "200px" }}
                defaultValue={{ value: "", label: "All label" }}
                options={[{ value: "", label: "All label" }, ...options]}
                onChange={handleChangeLabel}
              />
            </Form.Item>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Item
              label="Platform"
              style={{ width: "30%" }}
              name="platform"
            >
              <Select
                style={{ width: "200px" }}
                defaultValue={""}
                options={[
                  { value: "", label: "All platforms" },
                  { value: "true", label: "Mixinit only" },
                  { value: "false", label: "CC only" },
                ]}
                onChange={handleChangePlatform}
              />
            </Form.Item>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Item
              label="Contributors"
              style={{ width: "30%" }}
              name="contributors"
            >
              <Select
                placeholder="Select contributors"
                mode="multiple"
                showSearch
                filterOption={(input, option: any) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={optionsContributors}
                style={{ width: "200px" }}
                onChange={(e) => handleChangeContributors(e)}
              />
            </Form.Item>
          </div>
          <TextField
            value={`Total Earnings: $${
              totalEarningsExport.toFixed(2) || "$0.00"
            }`}
            style={{
              color: "#626262",
              fontSize: "14px",
              fontWeight: 700,
              marginLeft: "50px",
            }}
          />
        </div>
        <div>
          <CSVLink {...csvReport}>
            <Button disabled={loading}>
              <ExportOutlined />
              CSV
            </Button>
            <CSVLink {...csvReport}></CSVLink>
          </CSVLink>
        </div>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
      >
        <Button
          style={{
            color: "white",
            minWidth: "165px",
            fontWeight: "500",
            backgroundColor: "#52cb10",
          }}
          disabled={isLoadingPayout}
          loading={isLoadingPayout}
          onClick={() => handleAllPayout()}
        >
          Process All
        </Button>
      </div>

      <div className={styles.OverviewBox}>
        {/* <div style={{display: "flex", justifyContent: "end", marginBottom: "10px"}}>
                    <Button type="primary" onClick={handleAllProcessPayout}>
                        PROCESS PAYOUT
                    </Button>
                </div> */}
        <div
          style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap" }}
        >
          <Input
            onChange={debouncedSetFilter}
            placeholder="Search contributor, paypal email"
          />
        </div>
        <div className={styles.box}>
          <Table
            {...tableProps}
            className={styles.table}
            pagination={false}
            {...dataTable}
            scroll={{ x: true }}
            rowKey="id"
          >
            <Table.Column
              sorter
              dataIndex="contributor.username"
              key="contributor.username"
              title="Contributor"
              render={(_, record: any) => (
                <Link
                  target="_blank"
                  to={`/users/edit/${record?.contributor?._id}`}
                >
                  {record?.contributor?.username}
                </Link>
              )}
              defaultSortOrder={getDefaultSortOrder("contributor")}
            />
            <Table.Column
              sorter
              dataIndex="uploads"
              key="uploads"
              title="Uploads"
              render={(value) => <TextField value={value || ""} />}
            />
            <Table.Column
              sorter
              dataIndex="contributor.paypalEmail"
              key="contributor.paypalEmail"
              title="Paypal Email"
              render={(_, record: any) => (
                <TextField value={record?.contributor?.paypalEmail || ""} />
              )}
            />
            <Table.Column
              sorter
              dataIndex="trackEarnings"
              key="trackEarnings"
              title="Track Earnings"
              render={(_, record: any) => (
                <TextField
                  value={`$${(+record?.trackEarnings || 0).toFixed(2)}`}
                />
              )}
            />
            <Table.Column
              sorter
              dataIndex="Track DL’s"
              key="tracksSold"
              title="Track DL’s"
              render={(_, record: any) => (
                <Link
                  to={`/accounting-period/show/${record._id}?sub-download=false`}
                >
                  <TextField value={`${+record?.tracksSold || 0}`} />
                </Link>
              )}
            />
            <Table.Column
              sorter
              dataIndex="releaseEarnings"
              key="releaseEarnings"
              title="Release Earnings"
              render={(_, record: any) => (
                <TextField
                  value={`$${(+record?.releaseEarnings || 0).toFixed(2)}`}
                />
              )}
            />
            <Table.Column
              sorter
              dataIndex="subscriptionEarnings"
              key="subscriptionEarnings"
              title="Subscription Earnings"
              render={(_, record: any) => (
                <TextField
                  value={`$${(+record?.subscriptionEarnings || 0).toFixed(2)}`}
                />
              )}
            />
            <Table.Column
              sorter
              dataIndex="Subscription DL’s"
              key="numberSubscriptions"
              title="Subscription DL’s"
              render={(_, record: any) => (
                <Link
                  to={`/accounting-period/show/${record._id}?sub-download=true`}
                >
                  <TextField value={`${+record?.numberSubscriptions || 0}`} />
                </Link>
              )}
            />
            <Table.Column
              sorter
              dataIndex="totalEarningsExport"
              key="totalEarningsExport"
              title="Total Earnings"
              render={(_, record: any) => (
                <TextField
                  value={`$${(+record?.totalEarningsExport || 0).toFixed(2)}`}
                />
              )}
            />
            <Table.Column
              sorter
              dataIndex="name"
              key="name"
              title="Accounting Period"
              render={(value) => <TextField value={value} />}
            />
            <Table.Column
              sorter
              dataIndex="isAlreadyPayout"
              key="isAlreadyPayout"
              title="Status"
              render={(_, record: any) => (
                <TextField
                  value={record.isAlreadyPayout ? "PAID" : "NOT PAID"}
                />
              )}
            />
            <Table.Column
              sorter
              dataIndex="isAlreadyPayout"
              key="isAlreadyPayout"
              title="Process Payout"
              render={(_, record: any) => (
                <Button
                  style={{
                    color: record?.isAlreadyPayout ? "white" : "black",
                    minWidth: "165px",
                    fontWeight: "500",
                    backgroundColor: record?.metQuota ? "#52cb10" : "red",
                  }}
                  disabled={isLoadingPayout || record?.isAlreadyPayout}
                  onClick={() => handlePayout(record?._id)}
                >
                  {isLoadingPayout && accountingId === record?._id ? (
                    <Spin />
                  ) : record?.isAlreadyPayout ? (
                    "PROCESSED"
                  ) : (
                    "PROCESS PAYOUT"
                  )}
                </Button>
              )}
            />
            <Table.Column
              dataIndex="action"
              key="action"
              title="Action"
              render={(_, record: any) => (
                <Button
                  style={{ padding: 0, width: "24px", height: "24px" }}
                  onClick={() => showModal(record._id, record.uploads)}
                >
                  <EditOutlined size={16} />
                </Button>
              )}
            />
          </Table>
        </div>
      </div>
      <Modal
        title=""
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button type="primary" onClick={handleOk}>
            Save
          </Button>,
        ]}
      >
        <div>
          <div>Uploads</div>
          <Input
            value={uploads}
            onChange={(e: any) => setUploads(+e.target.value)}
            type="number"
            min={0}
            placeholder="Uploads"
            style={{ width: "100%" }}
          />
        </div>
      </Modal>
    </div>
  );
};
